import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Dashboard from './Dashboard';
import RegisterDashboardInsightsView from './RegisterDashboardInsightsView';
import updateDashboard from '../../api/updateDashboard';
import { useLocation, useNavigate } from 'react-router-dom';
import appRoutes, { buildDashboardShow } from '../../navigation/appRoutes';
import { DateTime } from 'luxon';

import archiveDashboard from '../../api/archiveDashboard';
import withoutNulls from '../../api/expression/withoutNulls';
import createDashboardGadget from '../../api/createDashboardGadget';
import useWindowSize from '../../hooks/useWindowSize';
import { getCanvasMode } from '../ReportCanvas';
import PopupProvider from '../../contextProviders/PopupProvider';
import DashboardContext from '../../contexts/DashboardContext';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import Loading from 'components/Loading';
import VariableFiltersContext from '../../contexts/VariableFiltersContext';
import usePopup from '../../hooks/usePopup';
import useHasAccess from 'hooks/useHasAccess';
import CurrentUserContext from '../../contexts/CurrentUserContext';
import BoardsProvider from '../../contextProviders/SplashScreenProviders/ContentProviders/BoardsProvider';
import useContentViewTracking from '../../hooks/useContentViewTracking';
import useRegisterInsightsContent from '../../hooks/useRegisterInsightsContent';
import PopupContext from '../../contexts/PopupContext';
import ReportsContext from '../../contexts/ReportsContext';
import DashboardsContext from 'contexts/DashboardsContext';
import aguid from 'lib/aguid';
import buildTemplateNotFoundError from './buildTemplateNotFoundError';
import captureException from '../../services/captureException';
import BrokenDashboardTemplate from './BrokenDashboardTemplate';
import WorkSpaceContext from '../../contexts/WorkSpaceContext';
import CopyGadgetProvider from '../../contextProviders/CopyGadgetProvider';
import { DASHBOARDS_COLLECTION } from '../../constants';
import WallboardContext from '../../contexts/WallboardContext';
import reloadWhenSafe from '../../reloadWhenSafe';
import AccountPickerContext from '../../contexts/AccountPickerContext';
import GqlClientProvider from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';
import RolesContext from '../../contexts/RolesContext';
import useCreateDashboardCopy from '../../hooks/useCreateDashboardCopy';
import LocalTimelineProvider from '../../contextProviders/TimelineProvider/LocalTimelineProvider';
import TargetsAppContext from '../../screens/TargetsAppShow/TargetsAppConfigured/TargetsAppContext';
import VariableRuntimeFilters from './VariableRuntimeFilters';
import isDashboardTemplate from '../../types/isDashboardTemplate';
import EntityDetailsContext from '../../screens/EntityDetailsShow/EntityDetailsContext';
import { PortalsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import useRefreshDatasetFreshnessOnMount from '../../hooks/useRefreshDatasetFreshnessOnMount';
import useIsEngagementMode from '../../hooks/useIsEngagementMode';
import useIsInvalidFilterInput from '../GroupVsRemainder/hooks/useIsInvalidFilterInput';
import MetricsGate from '../MetricsGate';
import VisualisationsGate from '../VisualisationsGate';
import ToastContext from '../../contexts/ToastContext';
import ReportContext from '../../contexts/ReportContext';
import CloudFunctionClientContext from '../../contexts/CloudFunctionClientContext';
import exportDashboardHeadless from './exportDashboardHeadless';
import DashboardGadgetsContext from '../../contexts/DashboardGadgetsContext';
import ReportGadgetsContext from '../../contexts/ReportGadgetsContext';
import buildScheduledNotes from '../Report/buildScheduledNotes';
import usePublishPendingCsvs from '../Report/usePublishPendingCsvs';
import waitForGridExportCallbacks, {
  getExpectedGridExportCards,
} from '../Report/waitForGridExportCallbacks';
import DashboardFiltersProvider, {
  DashboardFiltersContext,
} from '../../contextProviders/DashboardFiltersProvider';

const usePopupReportId = () =>
  new URLSearchParams(useLocation().search).get('popupReportId');

const hasEditingSearchQuery = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const isEditing =
    urlParams.has('editing') && urlParams.get('editing') === 'true';
  return isEditing;
};

export const useTemplatedForm = (dashboard?: DashboardType) => {
  const { accountRef } = useContext(AccountPickerContext);
  const { allDashboards, isLoading: isLoadingAllDashboards } =
    useContext(DashboardsContext);
  const [isBrokenTemplated, setIsBrokenTemplated] = useState<boolean>(false);
  const [templatedFrom, setTemplatedFrom] = useState<
    DashboardTemplate | undefined
  >();

  useEffect(() => {
    if (dashboard && dashboard.templateId) {
      if (!isLoadingAllDashboards && allDashboards.length > 0) {
        const t = allDashboards
          .filter(isDashboardTemplate)
          .find((d) => d.id === dashboard.templateId);
        setTemplatedFrom(t);
        const isBroken = t === undefined;
        setIsBrokenTemplated(isBroken);
      } else {
        if (process.env.NODE_ENV === 'test') {
          return;
        }

        let isActive = true;
        accountRef
          .collection(DASHBOARDS_COLLECTION)
          .doc(dashboard.templateId)
          .get()
          .then((doc) => {
            if (!isActive) {
              return;
            }
            if (doc.exists) {
              const dash = { ...doc.data() } as DashboardTemplate;
              setTemplatedFrom(dash);
            } else {
              setIsBrokenTemplated(true);
            }
          });

        return () => {
          isActive = false;
        };
      }
    }
  }, [accountRef, allDashboards, dashboard, isLoadingAllDashboards]);

  useEffect(() => {
    if (dashboard && isBrokenTemplated && dashboard.templateId) {
      const error = buildTemplateNotFoundError({
        templatedDashboardId: dashboard.id,
        dashboardTemplateId: dashboard.templateId,
      });
      captureException(error);
    }
  }, [dashboard, isBrokenTemplated]);

  return {
    isBrokenTemplated,
    templatedFrom,
  };
};

const DashboardContainer = ({
  dashboard,
  onDeleted,
  templatedFrom,
  isBrokenTemplated,
  forceHeadlessRender,
}: {
  dashboard: DashboardType;
  onDeleted?: () => void;
  templatedFrom?: DashboardTemplate;
  isBrokenTemplated?: boolean;
  forceHeadlessRender?: boolean;
}) => {
  const getCanvas = useCallback(() => {
    if (dashboard.templateId) {
      if (templatedFrom) {
        return templatedFrom.canvas;
      }
    }

    return dashboard.canvas;
  }, [dashboard.canvas, dashboard.templateId, templatedFrom]);

  // Context
  const { selectedPortal } = useContext(PortalsContext);
  const currentUser = useContext(CurrentUserContext);
  const { allReports } = useContext(ReportsContext);
  const { workSpace } = useContext(WorkSpaceContext);
  const targetsAppContext = useContext(TargetsAppContext);
  const entityDetailsContext = useContext(EntityDetailsContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { wallboard, isWallboard, isWallboardBuilder } =
    useContext(WallboardContext);
  const { openPopupReport } = useContext(PopupContext);
  const { isOpen: isPopupReportOpen } = useContext(PopupContext);
  const { addOrUpdateDashboard } = useContext(DashboardsContext);
  const { accountRef, selectedAccountId } = useContext(AccountPickerContext);
  const { currentPermissions } = useContext(RolesContext);
  const { isEngagementMode } = useIsEngagementMode();
  const { showToast } = useContext(ToastContext);
  const { api } = useContext(CloudFunctionClientContext);
  // The dashboard's own filters (DashboardFiltersProvider, in Gate)
  const dashboardFilters = useContext(DashboardFiltersContext);

  // Per-gadget CSV export callbacks, collected via ReportContext by the shared
  // GridCsvExportRegistrar on each matrix/grid gadget. Only populated on the
  // headless PDF path (isPdfExport), consumed by the scheduled-delivery export.
  const [gridExportCallbacks, setGridExportCallbacks] = useState<
    GridExportCallback[]
  >([]);
  // Latest registered callbacks, readable from inside an export that is already
  // running (the headless export waits for late-registering grids).
  const gridExportCallbacksRef = useRef<GridExportCallback[]>([]);
  gridExportCallbacksRef.current = gridExportCallbacks;
  const { dashboardGadgetsLookup } = useContext(DashboardGadgetsContext);
  const { reportGadgetsLookup } = useContext(ReportGadgetsContext);

  // State
  const { access } = dashboard;
  const [previewCanvasMode, setPreviewCanvasMode] = useState<
    CanvasMode | undefined
  >();
  const [isSavingAs, setIsSavingAs] = useState<boolean>(false);
  const [currentCanvas, setCurrentCanvas] = useState<Canvas>(() => getCanvas());
  // A scheduled render waits for every grid's CSV
  usePublishPendingCsvs({
    isPdfExport: !!forceHeadlessRender,
    cards: currentCanvas.cards,
    gridExportCallbacks,
    reportGadgetsLookup,
    dashboardGadgetsLookup,
  });
  const [isEditing, setIsEditing] = useState<boolean>(hasEditingSearchQuery());
  const [isEditingCard, setIsEditingCard] = useState<boolean>(false);
  const [variableFilters, setVariableFilters] = useState<
    VariableDrillDownType[]
  >(() => dashboard.variableDrillDowns);
  // We change the key to trigger a refresh
  const [key, setKey] = useState<string>(aguid());
  const [isPopupGridOpen, setIsPopupGridOpen] = useState<boolean>(false);

  // Hooks
  const location = useLocation();
  const navigate = useNavigate();
  useContentViewTracking({
    type: 'dashboard',
    typeId: dashboard.id,
    name: dashboard.name,
  });
  // Publish the ENTITY to the AI Insights Chat so it can scope answers to this
  // dashboard. Skipped on wallboards (passive, multi-slide displays).
  //
  // The live VIEW (on-screen gadget structure + each gadget's own resolved date
  // range/filters/figures) is published separately by RegisterDashboardInsightsView
  // wrapping the canvas below — a dashboard has no dashboard-level scope, so its
  // view is a set of per-gadget scopes captured deep in the tree, unlike a report's
  // single report-level scope (see RegisterReportInsightsView). The entity is
  // registered here, above the metric/vis gates, so it survives loading.
  useRegisterInsightsContent(
    isWallboard
      ? null
      : {
          entityType: 'dashboard',
          entityId: dashboard.id,
          entityName: dashboard.name,
          content: { contentType: 'Dashboard', content: dashboard },
        },
  );
  useRefreshDatasetFreshnessOnMount();
  // Edit/admin chrome only collapses on phone-width windows. The canvas itself
  // reflows by its own width (see ReportCanvas getLayoutTier).
  const { useMobileLayout: isMobile } = useWindowSize();
  const {
    isOpen: isVariableFiltersSlideOutOpen,
    open: openVariableFiltersSlideOut,
    close: closeVariableFiltersSlideOut,
  } = usePopup();
  const popupReportId = usePopupReportId();
  const createDashboardCopy = useCreateDashboardCopy();
  const hasAccess = useHasAccess();
  const hasInvalidFilters = useIsInvalidFilterInput(getCanvas());

  useEffect(() => {
    if (hasInvalidFilters) {
      showToast(
        'Unsupported entity filters detected, Group Vs Remainder gadgets disabled',
      );
    }
  }, [hasInvalidFilters, showToast]);

  // Effects
  // Keep variable filters in sync with what is persisted
  useEffect(() => {
    setVariableFilters(dashboard.variableDrillDowns);
  }, [dashboard.variableDrillDowns]);

  // Headless scheduled delivery: expose the in-page export hook the renderer
  // calls once the dashboard has painted. It uploads the per-gadget CSVs
  // (collected via ReportContext below) and shares the renderer-supplied PDF +
  // preview. Only mounted under PrintDashboardShow (forceHeadlessRender).
  useEffect(() => {
    if (!forceHeadlessRender) return;
    window.__fleetopsExportReport = async ({
      recipientUserIds,
      senderId,
      notes,
      pdfPath,
      reportImagePath,
      exportId,
      missingGadgets,
    }) => {
      if (!pdfPath || !exportId) return;
      const savedFilterId =
        new URLSearchParams(window.location.search).get('savedFilterId') ?? '';
      // No one is watching, so wait for every grid gadget to register its CSV
      // rather than sending whatever has registered so far.
      const { callbacks, missingCsvs } = await waitForGridExportCallbacks({
        expectedCards: getExpectedGridExportCards({
          cards: currentCanvas.cards,
          reportGadgetsLookup,
          dashboardGadgetsLookup,
        }),
        getGridExportCallbacks: () => gridExportCallbacksRef.current,
      });
      const excelPaths = await exportDashboardHeadless({
        dashboard,
        accountId: selectedAccountId,
        api,
        senderId,
        recipientUserIds,
        notes: buildScheduledNotes({
          notes,
          missingGadgets: missingGadgets ?? [],
          contentLabel: 'dashboard',
        }),
        savedFilterId,
        gridExportCallbacks: callbacks,
        headless: { pdfPath, reportImagePath, exportId },
      });
      // The renderer records these on the run
      return { excelPaths, missingCsvs };
    };
    return () => {
      window.__fleetopsExportReport = undefined;
    };
  }, [
    api,
    currentCanvas.cards,
    dashboard,
    dashboardGadgetsLookup,
    forceHeadlessRender,
    reportGadgetsLookup,
    selectedAccountId,
  ]);

  // Set canvas to the template if applicable
  useEffect(() => {
    if (popupReportId) {
      const popupReport = allReports.find((r) => r.id === popupReportId);
      if (popupReport) {
        openPopupReport({
          drillDowns: [],
          origin: 'Dashboard',
          selectedReport: popupReport,
        });
      }
    }
  }, [allReports, openPopupReport, popupReportId]);

  useEffect(() => {
    trackEvent('Dashboard - Opened', {
      dashboardName: dashboard.name,
      dashboardId: dashboard.id,
      accessType: dashboard.access.type,
      source: workSpace ? 'Workspace' : 'Dashboard',
    });
  }, [
    dashboard.access.type,
    dashboard.id,
    dashboard.name,
    trackEvent,
    workSpace,
  ]);

  useEffect(() => {
    closeVariableFiltersSlideOut();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // Refresh every 15 min
  useEffect(() => {
    if (
      !isWallboard &&
      !isEditing &&
      !isEditingCard &&
      !isPopupReportOpen &&
      !isPopupGridOpen
    ) {
      const refreshInterval = setInterval(
        () => {
          reloadWhenSafe(() => {
            trackEvent('Dashboard - Auto Refreshed', {
              dashboardName: dashboard.name,
              dashboardId: dashboard.id,
            });
            setKey(aguid());
          });
        },
        1000 * 60 * 15,
      );

      return () => {
        clearInterval(refreshInterval);
      };
    }
  }, [
    isPopupGridOpen,
    isPopupReportOpen,
    dashboard.id,
    dashboard.name,
    isEditing,
    isEditingCard,
    trackEvent,
    isWallboard,
  ]);

  useEffect(() => {
    if (
      access &&
      !workSpace &&
      !targetsAppContext &&
      !entityDetailsContext &&
      !isWallboard
    ) {
      if (
        !hasAccess({
          access,
          resource: dashboard,
          type: 'dashboard',
          typeId: dashboard.id,
        })
      ) {
        navigate(appRoutes.home);
      }
    }
  }, [
    entityDetailsContext,
    access,
    currentUser,
    dashboard,
    workSpace,
    isWallboard,
    currentPermissions,
    hasAccess,
    targetsAppContext,
    navigate,
  ]);

  // Callbacks
  const onEditSave = useCallback(async () => {
    const newDashboard = {
      ...dashboard,
      canvas: currentCanvas,
      ...(dashboardFilters ? { scope: dashboardFilters.scope } : {}),
      updatedBy: currentUser.id,
      updatedOn: DateTime.utc().toISO(),
    } as DashboardType;
    trackEvent('Dashboard - Edit - Saved', {
      isWallboardSlide: isWallboard ? 'true' : 'false',
    });

    await updateDashboard({
      id: dashboard.id,
      newDashboard: withoutNulls(newDashboard),
      accountRef,
    });
    setIsEditing(false);
    setPreviewCanvasMode(undefined);
    addOrUpdateDashboard(newDashboard);

    if (hasEditingSearchQuery()) {
      navigate(location.pathname);
    }
  }, [
    accountRef,
    addOrUpdateDashboard,
    currentCanvas,
    currentUser.id,
    dashboard,
    dashboardFilters,
    location,
    navigate,
    isWallboard,
    trackEvent,
  ]);

  const saveEditAs = useCallback(
    async (name: string, labelIds: string[]) => {
      const { newDashboard } = await createDashboardCopy({
        name,
        dashboard: {
          ...dashboard,
          canvas: currentCanvas,
          ...(dashboardFilters ? { scope: dashboardFilters.scope } : {}),
          isEntityDetailsTab: false,
        },
        access: dashboard.access,
        labelIds,
      });

      navigate(
        buildDashboardShow({ id: newDashboard.id, portal: selectedPortal }),
      );
    },
    [
      createDashboardCopy,
      dashboard,
      currentCanvas,
      dashboardFilters,
      navigate,
      selectedPortal,
    ],
  );

  // Misc
  const canvasMode =
    previewCanvasMode ||
    getCanvasMode({
      isMobile,
    });

  const startEditing = useCallback(() => {
    trackEvent('Dashboard - Edit - Started', {
      isWallboardSlide: isWallboard ? 'true' : 'false',
    });
    // Edit the saved filters, without the viewer's run-time ones on top
    dashboardFilters?.reset();
    setIsEditing(true);
  }, [dashboardFilters, isWallboard, trackEvent]);

  const createAndAddDashboardWidget = useCallback(
    async (gadget: DashboardGadget, card: CanvasCard.Card) => {
      await createDashboardGadget(gadget, selectedAccountId);
      const newDashboard = {
        ...dashboard,
        updatedOn: DateTime.utc().toISO(),
        updatedBy: currentUser.id,
        canvas: {
          ...dashboard.canvas,
          cards: [...dashboard.canvas.cards, card],
        },
      };

      await updateDashboard({
        id: dashboard.id,
        newDashboard,
        accountRef,
      });
      setCurrentCanvas((c) => ({
        ...c,
        cards: [...c.cards, card],
      }));
      addOrUpdateDashboard(newDashboard);

      return;
    },
    [
      accountRef,
      addOrUpdateDashboard,
      currentUser.id,
      dashboard,
      selectedAccountId,
    ],
  );

  const onEditCancel = useCallback(() => {
    trackEvent('Dashboard - Edit - Cancelled', {
      isWallboardSlide: isWallboard ? 'true' : 'false',
    });
    setIsEditing(false);
    setPreviewCanvasMode(undefined);
    setCurrentCanvas(getCanvas());
    dashboardFilters?.reset();
    if (hasEditingSearchQuery()) {
      navigate(location.pathname);
    }
  }, [
    trackEvent,
    isWallboard,
    getCanvas,
    dashboardFilters,
    navigate,
    location.pathname,
  ]);

  const saveDashboardAs = useCallback(
    async (name: string, labelIds: string[]) => {
      setIsSavingAs(true);
      const { newDashboard } = await createDashboardCopy({
        name,
        dashboard: { ...dashboard, isEntityDetailsTab: false },
        templatedFrom,
        access: dashboard.access,
        labelIds,
      });
      setIsSavingAs(false);
      navigate(
        buildDashboardShow({
          id: newDashboard.id,
          portal: selectedPortal,
        }),
      );
    },
    [selectedPortal, createDashboardCopy, dashboard, navigate, templatedFrom],
  );

  const createTemplateFrom = useCallback(async () => {
    setIsSavingAs(true);
    const { newDashboard } = await createDashboardCopy({
      name: dashboard.name,
      dashboard: { ...dashboard, isEntityDetailsTab: false },
      templatedFrom: undefined,
      isCreatingTemplate: true,
      access: dashboard.access,
    });

    setIsSavingAs(false);
    navigate(
      buildDashboardShow({
        id: newDashboard.id,
        portal: selectedPortal,
      }),
    );
  }, [selectedPortal, createDashboardCopy, dashboard, navigate]);

  const onDelete = useCallback(() => {
    archiveDashboard(dashboard.id, selectedAccountId).then(() => {
      if (onDeleted) {
        onDeleted();
      } else {
        navigate(appRoutes.loggedIn.dashboards);
        alert('Dashboard archived');
      }
    });
  }, [selectedAccountId, dashboard.id, navigate, onDeleted]);

  if (!currentUser) {
    return null;
  }

  if (!dashboard) {
    return <Loading />;
  }

  if (isBrokenTemplated) {
    return <BrokenDashboardTemplate />;
  }

  if (popupReportId) {
    return null;
  }

  const dashboardTree = (
    <DashboardContext.Provider
      value={{
        dashboard,
        setIsEditingCard,
        setIsPopupGridOpen,
        templatedFrom,
      }}
    >
      <GqlClientProvider>
        <PopupProvider>
          <VariableFiltersContext.Provider
            value={{
              variableFilters,
              setVariableFilters,
            }}
          >
            <CopyGadgetProvider
              currentDashboard={{
                id: dashboard.id,
                setCurrentCanvas,
                setIsEditing,
              }}
            >
              <BoardsProvider>
                <VariableRuntimeFilters dashboard={dashboard} />
                <RegisterDashboardInsightsView
                  dashboardId={dashboard.id}
                  cards={currentCanvas.cards}
                  isDisabled={isWallboard}
                >
                  <Dashboard
                    key={key}
                    currentCanvas={currentCanvas}
                    setCurrentCanvas={setCurrentCanvas}
                    isEditing={isEditing}
                    onEditSave={onEditSave}
                    onEditCancel={onEditCancel}
                    saveDashboardAs={saveDashboardAs}
                    createTemplateFrom={createTemplateFrom}
                    saveEditAs={saveEditAs}
                    startEditing={startEditing}
                    onDelete={onDelete}
                    createAndAddDashboardWidget={createAndAddDashboardWidget}
                    canvasMode={canvasMode}
                    previewCanvasMode={previewCanvasMode}
                    isSavingAs={isSavingAs}
                    isWallboard={!!wallboard}
                    isWallboardBuilder={isWallboardBuilder}
                    isMobile={isMobile}
                    dashboardId={dashboard.id}
                    dashboard={dashboard}
                    isVariableFiltersSlideOutOpen={
                      isVariableFiltersSlideOutOpen
                    }
                    openVariableFiltersSlideOut={openVariableFiltersSlideOut}
                    closeVariableFiltersSlideOut={closeVariableFiltersSlideOut}
                    isTargetsApp={!!targetsAppContext}
                    isEntityApp={!!entityDetailsContext}
                    isEngagementMode={isEngagementMode}
                  />
                </RegisterDashboardInsightsView>
              </BoardsProvider>
            </CopyGadgetProvider>
          </VariableFiltersContext.Provider>
        </PopupProvider>
      </GqlClientProvider>
    </DashboardContext.Provider>
  );

  // On the headless scheduled-delivery path, provide a ReportContext so the
  // shared canvas runs in PDF mode (refit + fixed-height sizing) and the matrix
  // gadgets' GridCsvExportRegistrar collects per-gadget CSV callbacks. Live
  // dashboards render without it (unchanged behaviour).
  if (forceHeadlessRender) {
    return (
      <ReportContext.Provider
        value={{
          currentScope: [],
          currentDrillDowns: [],
          reset: () => {},
          quickFilters: [],
          selectedQuickFilters: [],
          setSelectedQuickFilters: () => {},
          gridExportCallbacks,
          setGridExportCallbacks,
          isHeadlessRender: true,
          isPdfExport: true,
        }}
      >
        {dashboardTree}
      </ReportContext.Provider>
    );
  }

  return dashboardTree;
};

const Gate = ({
  dashboard,
  onDeleted,
  forceHeadlessRender,
}: {
  dashboard: DashboardType;
  onDeleted?: () => void;
  // Set only by PrintDashboardShow (the chrome-less scheduled-delivery route).
  // Drives the canvas into headless PDF mode and exposes the export hook.
  forceHeadlessRender?: boolean;
}) => {
  const { isBrokenTemplated, templatedFrom } = useTemplatedForm(dashboard);
  const gateContent = useMemo(
    () =>
      templatedFrom
        ? { ...dashboard, canvas: templatedFrom.canvas }
        : dashboard,
    [dashboard, templatedFrom],
  );

  if (dashboard.templateId && !templatedFrom && !isBrokenTemplated) {
    return <Loading />;
  }

  return (
    <VisualisationsGate content={gateContent} contentType="Dashboard">
      <MetricsGate content={gateContent} contentType="Dashboard">
        <LocalTimelineProvider sources={window.emptyArray}>
          <DashboardFiltersProvider dashboard={dashboard}>
            <DashboardContainer
              dashboard={dashboard}
              templatedFrom={templatedFrom}
              isBrokenTemplated={isBrokenTemplated}
              onDeleted={onDeleted}
              forceHeadlessRender={forceHeadlessRender}
            />
          </DashboardFiltersProvider>
        </LocalTimelineProvider>
      </MetricsGate>
    </VisualisationsGate>
  );
};

export default Gate;
